import React, { useState, useEffect } from 'react';
import Layout from '../../components/user/Layout';
import authManager from '../../utils/authManager';
import axios from '../../utils/axios'; 
import { formatCurrency } from '../../utils/helper';
import PaymentOptions from '../../components/web/form/PaymentOptions';

function PaymentWallet() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
    const [showPayment, setShowPayment] = useState(false);


    const user = authManager.getUserProfile();


  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await axios.get('/member/payments');
        if (response.data.success) {
          setPaymentData(response.data.data);
        } else {
          setError('Failed to load payment data');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="text-center text-red-600 py-10">{error}</div>
      </Layout>
    );
  }
  
   const handleApply = () => {
      if (!user?.is_total_paid) {
        setShowPayment(true);
        return;
      }

    };

  const { member, summary, payments } = paymentData;

  return (
 <Layout>
  {showPayment && (
    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black/50">
      <PaymentOptions />
    </div>
  )}

  <div className="min-h-screen py-12 px-4">
    <div className="max-w-6xl mx-auto space-y-10">
      <div className="flex justify-between items-center bg-white/80 backdrop-blur-md rounded-3xl shadow-lg p-8">
        <div className="">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Payment Details</h1>
          <p className="text-gray-600">
            {member.name} ({member.union_code}) – {member.department}
          </p>
        </div>
        {
          user.is_total_paid ? null : 
          <button onClick={()=>setShowPayment(true)} className='bg-green-500 text-white px-3 py-1 rounded-sm'>
            Pay now
          </button> 
        }
      </div>

      {/* Warning Banner for 2+ Years Pending Payment */}
      {(() => {
        const currentYear = new Date().getFullYear();
        const oldestPendingYear = payments
          .filter(p => !p.is_paid)
          .map(p => parseInt(p.year))
          .sort((a, b) => a - b)[0];
        
        const yearsPending = oldestPendingYear ? currentYear - oldestPendingYear : 0;
        
        if (yearsPending >= 2 && !user.is_total_paid) {
          return (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-red-800 font-semibold">
                    ⚠️ Your payment has been pending for {yearsPending} years!
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    As per rules, a <span className="font-bold text-red-900">Re-admission Fee of ₹2,500</span> will be charged along with your pending dues.
                  </p>
                  <p className="text-xs text-red-600 mt-2">
                    Please clear all pending payments to avoid further penalties.
                  </p>
                </div>
                <div className="ml-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    + ₹2,500 Fee
                  </span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition">
          <p className="text-sm opacity-90">Total Expected</p>
          <p className="text-3xl font-bold">
            ₹{formatCurrency(
              (summary.total_paid + summary.total_due) + 
              (() => {
                const currentYear = new Date().getFullYear();
                const oldestPendingYear = payments
                  .filter(p => !p.is_paid)
                  .map(p => parseInt(p.year))
                  .sort((a, b) => a - b)[0];
                const yearsPending = oldestPendingYear ? currentYear - oldestPendingYear : 0;
                return yearsPending >= 2 && !user.is_total_paid ? 2500 : 0;
              })()
            )}
          </p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition">
          <p className="text-sm opacity-90">Total Paid</p>
          <p className="text-3xl font-bold">₹{formatCurrency(summary.total_paid)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition">
          <p className="text-sm opacity-90">Total Due</p>
          <p className="text-3xl font-bold">
            ₹{formatCurrency(
              summary.total_due + 
              (() => {
                const currentYear = new Date().getFullYear();
                const oldestPendingYear = payments
                  .filter(p => !p.is_paid)
                  .map(p => parseInt(p.year))
                  .sort((a, b) => a - b)[0];
                const yearsPending = oldestPendingYear ? currentYear - oldestPendingYear : 0;
                return yearsPending >= 2 && !user.is_total_paid ? 2500 : 0;
              })()
            )}
          </p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600">
          <h2 className="text-xl font-semibold text-white">Payment History</h2>
        </div>
        <div className="p-6">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="pb-3 font-semibold text-gray-700">Year</th>
                <th className="pb-3 font-semibold text-gray-700">Amount (₹)</th>
                <th className="pb-3 font-semibold text-gray-700">Status</th>
               </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3">{payment.year}</td>
                  <td className="py-3">{formatCurrency(payment.amount)}</td>
                  <td className="py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        payment.is_paid
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {payment.status}
                    </span>
                  </td>
                </tr>
              ))}
              {/* Show Re-admission Fee Row if applicable */}
              {(() => {
                const currentYear = new Date().getFullYear();
                const oldestPendingYear = payments
                  .filter(p => !p.is_paid)
                  .map(p => parseInt(p.year))
                  .sort((a, b) => a - b)[0];
                const yearsPending = oldestPendingYear ? currentYear - oldestPendingYear : 0;
                
                if (yearsPending >= 2 && !user.is_total_paid) {
                  return (
                    <tr className="border-b border-red-200 bg-red-50">
                      <td className="py-3 font-semibold text-red-700">Re-admission Fee</td>
                      <td className="py-3 font-semibold text-red-700">₹2,500</td>
                      <td className="py-3">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Pending
                        </span>
                      </td>
                    </tr>
                  );
                }
                return null;
              })()}
            </tbody>
          </table>
          {payments.length === 0 && (
            <p className="text-center text-gray-500 py-4">No payment records found.</p>
          )}
        </div>
      </div>
    </div>
  </div>
</Layout>
  );
}

export default PaymentWallet;